import React, { useEffect, useState, useCallback } from 'react';
import API from '../lib/api';
import { usePeriod } from '../contexts/PeriodContext';
import { useAuth } from '../contexts/AuthContext';
import { StatusChip } from '../components/app/StatusChip';
import { SuggestionBadge } from '../components/app/SuggestionBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Search, Filter, RefreshCw, CheckSquare, ChevronLeft, ChevronRight, History, X, Send } from 'lucide-react';
import { LinkedFeeBadge } from '../components/app/LinkedFeeBadge';
import { RefundBadge } from '../components/app/RefundBadge';

const STATUS_OPTIONS = ['PENDING','CODED','SUBMITTED','APPROVED','REJECTED','NEEDS_REVIEW'];

function AllocationPanel({ txn, onClose, categories, costCenters, onSaved, readOnly }) {
  const [cat, setCat]   = useState(txn?.category_id || '');
  const [cc, setCC]     = useState(txn?.cost_center_id || '');
  const [desc, setDesc] = useState(txn?.description || '');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('code');

  useEffect(() => {
    if (!txn) return;
    setCat(txn.category_id || ''); setCC(txn.cost_center_id || ''); setDesc(txn.description || '');
    API.get(`/transactions/${txn.id}/history`).then(r => setHistory(r.data)).catch(() => {});
  }, [txn?.id]);

  const save = async () => {
    setSaving(true);
    try {
      await API.put(`/transactions/${txn.id}/allocate`, { category_id: cat || null, cost_center_id: cc || null, description: desc || null });
      toast.success('Allocation saved');
      onSaved();
    } catch(e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const applySuggestion = () => {
    if (txn.suggestion?.category_id) setCat(txn.suggestion.category_id);
    if (txn.suggestion?.cost_center_id) setCC(txn.suggestion.cost_center_id);
    toast.success('Suggestion applied');
  };

  if (!txn) return null;

  return (
    <SheetContent side="right" className="w-[420px] bg-white">
      <SheetHeader className="border-b pb-3">
        <SheetTitle className="text-sm">Transaction Detail</SheetTitle>
      </SheetHeader>
      <div className="overflow-y-auto h-full pb-20">
        {/* Transaction info */}
        <div className="p-4 space-y-1 border-b">
          <div className="text-xs text-slate-500">{txn.date}</div>
          <div className="font-semibold text-sm">{txn.description_raw}</div>
          <div className={`text-xl font-bold tabular-nums ${(txn.is_credit || txn.is_refund) ? 'text-emerald-600' : 'text-slate-900'}`}>
            {(txn.amount_with_fees || txn.amount)?.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' })}
            {txn.has_linked_fees && (
              <span className="ml-2 text-xs font-normal text-violet-600">
                (base {txn.amount?.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' })} + fees)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <StatusChip status={txn.status} size="xs" />
            {txn.is_refund && <RefundBadge transaction={txn} />}
            {txn.suggestion && <SuggestionBadge suggestion={txn.suggestion} />}
            {txn.has_linked_fees && <LinkedFeeBadge transaction={txn} />}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {['code', 'history'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-xs font-medium capitalize transition-colors
                ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>
              {t === 'history' ? <span className="flex items-center gap-1"><History size={11} />{t}</span> : t}
            </button>
          ))}
        </div>

        {tab === 'code' && (
          <div className="p-4 space-y-3">
            {txn.suggestion?.source_type !== 'needs_review' && (
              <div className="bg-blue-50 border border-blue-100 rounded p-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-blue-700">Suggestion available</span>
                  <button onClick={applySuggestion} className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold">Apply</button>
                </div>
                <p className="text-[11px] text-blue-600 mt-0.5">{txn.suggestion?.explanation}</p>
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-slate-600">Category</label>
              <Select value={cat} onValueChange={setCat} disabled={readOnly}>
                <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name} <span className="text-slate-400 font-mono text-xs ml-1">{c.gl_account}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Cost Center</label>
              <Select value={cc} onValueChange={setCC} disabled={readOnly}>
                <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select cost center" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {costCenters.map(c => <SelectItem key={c.id} value={c.id}>{c.name} <span className="text-slate-400 font-mono text-xs ml-1">{c.acumatica_code}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600">Description / Notes</label>
              <Textarea
                value={desc}
                onChange={e => setDesc(e.target.value)}
                className="mt-1 text-sm h-20 resize-none"
                placeholder="Add description or notes"
                disabled={readOnly}
              />
            </div>

            {!readOnly && (
              <Button size="sm" onClick={save} disabled={saving} className="w-full">
                {saving ? <><RefreshCw size={13} className="animate-spin mr-1" /> Saving…</> : 'Save Allocation'}
              </Button>
            )}
          </div>
        )}

        {tab === 'history' && (
          <div className="p-4">
            {history.length === 0 ? (
              <p className="text-xs text-slate-400">No allocation history</p>
            ) : (
              <div className="space-y-3">
                {history.map((h, i) => (
                  <div key={h.id || i} className="border rounded p-2.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">v{h.version}</span>
                      <span className="text-slate-400">{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</span>
                    </div>
                    <div className="mt-1 space-y-0.5">
                      <div><span className="text-slate-500">Category:</span> {h.category_name || h.category_id || '—'}</div>
                      <div><span className="text-slate-500">Cost Center:</span> {h.cost_center_name || h.cost_center_id || '—'}</div>
                      <div><span className="text-slate-500">By:</span> {h.coded_by}</div>
                      {h.description && <div><span className="text-slate-500">Note:</span> {h.description}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </SheetContent>
  );
}

export default function WorkbenchPage() {
  const { activePeriod } = usePeriod();
  const { user } = useAuth();
  const [txns, setTxns]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [selected, setSelected] = useState([]);
  const [selectedTxn, setSelectedTxn]   = useState(null);
  const [panelOpen, setPanelOpen]       = useState(false);
  const [categories, setCategories]     = useState([]);
  const [costCenters, setCostCenters]   = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction]     = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkCC, setBulkCC]             = useState('');
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const LIMIT = 50;
  const readOnly = user?.role === 'auditor';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (activePeriod?.id) params.period_id = activePeriod.id;
      if (search) params.search = search;
      if (statusFilter.length > 0) params.status = statusFilter.join(',');
      const res = await API.get('/transactions', { params });
      setTxns(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch(e) { toast.error('Failed to load transactions'); }
    finally { setLoading(false); }
  }, [activePeriod?.id, page, search, statusFilter]);

  useEffect(() => {
    load();
    API.get('/categories').then(r => setCategories(r.data)).catch(() => {});
    API.get('/cost-centers').then(r => setCostCenters(r.data)).catch(() => {});
  }, [load]);

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const selectAll = () => setSelected(txns.map(t => t.id));
  const clearSelect = () => setSelected([]);

  const doBulkAction = async () => {
    if (!bulkAction || selected.length === 0) return;
    try {
      await API.post('/transactions/bulk-action', {
        transaction_ids: selected,
        action: bulkAction,
        category_id: bulkCategory || null,
        cost_center_id: bulkCC || null,
      });
      toast.success(`Applied to ${selected.length} transactions`);
      setBulkDialogOpen(false); clearSelect(); load();
    } catch(e) { toast.error('Bulk action failed'); }
  };

  const doSubmitAll = async () => {
    try {
      const res = await API.post('/approvals/submit', { period_id: activePeriod?.id });
      toast.success(`Submitted ${res.data.submitted} transactions for approval`);
      setSubmitDialogOpen(false); load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
  };

  const pages = Math.ceil(total / LIMIT);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-bold">Transaction Workbench</h1>
        <div className="flex items-center gap-2">
          {selected.length > 0 && !readOnly && (
            <>
              <Button variant="outline" size="sm" onClick={() => setBulkDialogOpen(true)} data-testid="workbench-bulk-actions-button">
                <CheckSquare size={13} className="mr-1" /> Bulk ({selected.length})
              </Button>
              <Button variant="outline" size="sm" onClick={clearSelect}><X size={13} /></Button>
            </>
          )}
          {activePeriod && !readOnly && user?.role === 'finance_admin' && (
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                const res = await API.post(`/transactions/recompute-suggestions${activePeriod?.id ? `?period_id=${activePeriod.id}` : ''}`);
                toast.success(`Suggestions updated: ${res.data.auto_coded} auto-coded`);
                load();
              } catch(e) { toast.error('Recompute failed'); }
            }} title="Recompute suggestions using current rules">
              <RefreshCw size={13} className="mr-1" /> Recompute
            </Button>
          )}
          {activePeriod && !readOnly && (
            <Button size="sm" onClick={() => setSubmitDialogOpen(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="workbench-submit-button">
              <Send size={13} className="mr-1" /> Submit for Approval
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-3 bg-white border-b flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search merchant…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="h-8 pl-7 text-sm w-52"
            data-testid="workbench-search-input"
          />
        </div>
        <div className="flex flex-wrap gap-1" data-testid="workbench-status-filter">
          {STATUS_OPTIONS.map(s => (
            <button
              key={s}
              onClick={() => {
                setStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
                setPage(1);
              }}
              className={`text-xs px-2 py-0.5 rounded-full border transition-colors
                ${statusFilter.includes(s) ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >{s}</button>
          ))}
          {statusFilter.length > 0 && (
            <button onClick={() => setStatusFilter([])} className="text-xs text-slate-400 hover:text-slate-600">
              <X size={12} /> clear
            </button>
          )}
        </div>
        <span className="ml-auto text-xs text-slate-400">{total} transactions</span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="data-table" data-testid="workbench-table">
          <thead>
            <tr>
              <th className="w-8">
                <input type="checkbox" onChange={e => e.target.checked ? selectAll() : clearSelect()} />
              </th>
              <th>Status</th><th>Date</th><th>Merchant</th>
              <th className="text-right">Amount</th>
              <th>Cardholder</th><th>Category</th><th>Cost Center</th>
              <th>GL Account</th><th>Suggestion</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="text-center py-10"><RefreshCw size={18} className="animate-spin inline text-slate-400" /></td></tr>
            ) : txns.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-10 text-slate-400">No transactions found. Import a statement first.</td></tr>
            ) : txns.map(t => (
              <tr
                key={t.id}
                className={`cursor-pointer ${selected.includes(t.id) ? 'bg-blue-50' : ''}
                  ${['PENDING','NEEDS_REVIEW'].includes(t.status) && !t.is_credit ? 'exception' : ''}`}
                onClick={() => { setSelectedTxn(t); setPanelOpen(true); }}
              >
                <td onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggleSelect(t.id)} />
                </td>
                <td><StatusChip status={t.status} size="xs" /></td>
                <td className="whitespace-nowrap text-xs">{t.date}</td>
                <td className="max-w-xs">
                  <div className="truncate font-medium text-xs" title={t.description_raw}>{t.description_raw}</div>
                </td>
                <td className={`text-right tabular-nums font-medium text-sm ${t.is_credit ? 'text-emerald-600' : ''}`}>
                  {(t.amount_with_fees || t.amount)?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                </td>
                <td className="text-xs text-slate-500">{t.cardholder_name || '—'}</td>
                <td className="text-xs">{t.category_name || <span className="text-slate-300">Uncoded</span>}</td>
                <td className="text-xs">{t.cost_center_name || <span className="text-slate-300">—</span>}</td>
                <td className="font-mono text-xs text-slate-500">{t.gl_account || '—'}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    {t.is_refund && <RefundBadge transaction={t} />}
                    {t.suggestion && <SuggestionBadge suggestion={t.suggestion} />}
                    {t.has_linked_fees && <LinkedFeeBadge transaction={t} />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="px-6 py-3 bg-white border-t flex items-center justify-between">
          <span className="text-xs text-slate-500">Page {page} of {pages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>
              <ChevronLeft size={13} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p+1))} disabled={page === pages}>
              <ChevronRight size={13} />
            </Button>
          </div>
        </div>
      )}

      {/* Allocation Panel */}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <AllocationPanel
          txn={selectedTxn}
          onClose={() => setPanelOpen(false)}
          categories={categories}
          costCenters={costCenters}
          onSaved={() => { load(); setPanelOpen(false); }}
          readOnly={readOnly}
        />
      </Sheet>

      {/* Bulk action dialog */}
      <AlertDialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Bulk Action ({selected.length} transactions)</AlertDialogTitle>
            <AlertDialogDescription>Choose an action to apply to selected transactions.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 py-2">
            <Select value={bulkAction} onValueChange={setBulkAction}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select action" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="set_category">Set Category</SelectItem>
                <SelectItem value="set_cost_center">Set Cost Center</SelectItem>
                <SelectItem value="mark_needs_review">Mark Needs Review</SelectItem>
                <SelectItem value="submit">Submit for Approval</SelectItem>
              </SelectContent>
            </Select>
            {bulkAction === 'set_category' && (
              <Select value={bulkCategory} onValueChange={setBulkCategory}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent className="bg-white">{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {bulkAction === 'set_cost_center' && (
              <Select value={bulkCC} onValueChange={setBulkCC}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select cost center" /></SelectTrigger>
                <SelectContent className="bg-white">{costCenters.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doBulkAction}>Apply</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Submit dialog */}
      <AlertDialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Period for Approval?</AlertDialogTitle>
            <AlertDialogDescription>
              All coded transactions for {activePeriod?.label} will be submitted for finance review.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSubmitAll} className="bg-amber-600">Submit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
